import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { FEATURES } from 'appConstants';
import { makeSelectIsRoleTenantAdmin } from 'containers/App/selectors';
import { campaignCreate } from 'containers/Campaign/actions';
import { useFeatureFlags, useTypedSelector } from 'hooks';

import {
  CampaignWizardResult,
  CampaignWizardSubmission,
  CampaignWizardSubmit
} from '../wizardSubmission';

interface ICampaignCreateResponse {
  data?: { id?: number };
  meta?: { campaign_request_id?: number | null };
}

const errorMessage = (error: unknown): string => {
  const body = error as { message?: string; error?: { message?: string } } | undefined;
  return body?.message ?? body?.error?.message ?? '';
};

/**
 * The wizard's save through the campaign module's own create (`campaignCreate` → POST
 * /v4/dash/campaigns), called as `pages/campaign/create.tsx` calls it: a draft carries
 * `save_as_draft`, a confirm `state: 'active'`. It resolves the record and, under maker-checker,
 * its approval request; a failed create rejects with the API's message.
 */
const useCampaignWizardSave = (): CampaignWizardSubmit => {
  const dispatch = useDispatch();
  const flags = useFeatureFlags() as Record<string, boolean | undefined>;
  const isRoleTenantAdmin = useTypedSelector(makeSelectIsRoleTenantAdmin());
  const makerChecker = flags.maker_checker_campaigns === true && !isRoleTenantAdmin;
  const imagesForAllLanguages = flags[FEATURES.COMMON_IMAGES_FOR_ALL_LANGUAGES.key] === true;
  const multipleRaffleTickets = flags[FEATURES.MULTIPLE_RAFFLE_TICKETS.key] === true;

  return useCallback(
    async ({ intent, values }: CampaignWizardSubmission): Promise<CampaignWizardResult> => {
      const payload =
        intent === 'draft' ? { ...values, save_as_draft: 'true' } : { ...values, state: 'active' };
      const response = await new Promise<ICampaignCreateResponse>((resolve, reject) => {
        dispatch(
          campaignCreate(
            payload,
            null,
            makerChecker,
            imagesForAllLanguages,
            multipleRaffleTickets,
            (done?: unknown, error?: unknown) =>
              done !== undefined
                ? resolve(done as ICampaignCreateResponse)
                : reject(new Error(errorMessage(error)))
          )
        );
      });
      const campaignId = response.data?.id;
      if (campaignId === undefined) {
        throw new Error('');
      }
      return { campaignId, requestId: response.meta?.campaign_request_id ?? null };
    },
    [dispatch, makerChecker, imagesForAllLanguages, multipleRaffleTickets]
  );
};

export default useCampaignWizardSave;
